import {useCallback, useEffect, useMemo, useState, useRef, useContext} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useForm, Controller, useFieldArray} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row, Table, Modal, notification, AutoComplete, Empty, InputNumber} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import ActionBar from "~/layout/ActionBar";
import {Button, Loading} from "~/components";
import {InputField, DateField, TextAreaField, SelectSupplier} from "~/components/Forms";
import ProductOptionLabel from "~/components/Forms/ProductOptionLabel";
import KgWeightSelector from "../components/KgWeightSelector";
import {handleRequest, apiError} from "~/utils";
import {
    purchaseOrderApi,
    productApi
} from "~/api";
import '../style/PurchaseOrderForm.scss';
import {AppContext} from "../../../context/AppProvider";

const PurchaseOrderForm = () =>
{
    const {appData} = useContext(AppContext);
    const navigate = useNavigate();
    const {id} = useParams();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [searchProducts, setSearchProducts] = useState([]);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [forceUpdate, setForceUpdate] = useState(0);
    const searchTimeoutRef = useRef(null);
    const lastSelectedCodeRef = useRef(null); // Track mã sản phẩm vừa chọn
    const lastSearchKeywordRef = useRef(null); // Track keyword search cuối cùng
    const prevSearchKeywordRef = useRef(""); // Track giá trị trước đó để tránh loop
    const isSelectingRef = useRef(false); // Track đang trong quá trình select

    const initialValues = useMemo(() => ({
        code: "",
        purchase_date: dayjs(),
        supplier_id: null,
        note: "",
        details: [],
    }), []);

    const validationSchema = Yup.object().shape({
        purchase_date: Yup.date().required('Ngày nhập hàng không được để trống'),
    });

    const {control, handleSubmit, formState: {isSubmitting, errors}, reset, watch, setValue, trigger} = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema)
    });

    const {fields, append, remove} = useFieldArray({
        control,
        name: 'details'
    });

    // Load edit data
    useEffect(() => {
        if (isEdit) {
            loadPurchaseOrder();
        }

        // Cleanup timeout on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [id]);

    const loadPurchaseOrder = async () => {
        setLoading(true);
        let [error, response] = await handleRequest(purchaseOrderApi.get(id));
        setLoading(false);

        if (!apiError('Lấy thông tin phiếu nhập hàng thất bại', error, response)) {
            const data = response.data;
            reset({
                code: data.code ?? "",
                purchase_date: dayjs(data.purchase_date * 1000),
                supplier_id: data.supplier_id,
                note: data.note,
                details: data.details || [],
            });
        }
    };

    const handleSearchProducts = useCallback((value) => {

        // Bỏ qua TẤT CẢ nếu đang trong quá trình select
        if (isSelectingRef.current) {
            return;
        }

        // Clear previous timeout TRƯỚC KHI check
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }

        // Bỏ qua nếu value trùng với mã sản phẩm vừa chọn (AutoComplete tự động fill)
        if (value && lastSelectedCodeRef.current === value) {
            lastSelectedCodeRef.current = null; // Reset
            setSearchKeyword(""); // Clear input
            // KHÔNG đặt timeout search mới
            return; // Không search, giữ nguyên searchProducts
        }

        setSearchKeyword(value);

        if (!value || value.length < 2) {
            setSearchProducts([]);
            return;
        }

        // Set new timeout for debounce
        searchTimeoutRef.current = setTimeout(async () => {
            let [error, response] = await handleRequest(productApi.search(value));

            if (!apiError('Tìm kiếm sản phẩm thất bại', error, response))
            {
                const products = (response.data || []).map(product => ({
                    label: product.code,
                    value: `${product.code}`, // Dùng code (string) thay vì id (number)
                    product: product,
                }));
                setSearchProducts(products);
                lastSearchKeywordRef.current = value; // Lưu keyword search thành công
            }
        }, 500);
    }, []);

    const handleAddProduct = useCallback((value, option) => {
        const product = option.product;

        // ✅ QUAN TRỌNG: Clear timeout ngay để hủy search đang pending
        if (searchTimeoutRef.current)
        {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }

        // Set flag để ngăn mọi onChange event trong quá trình select
        isSelectingRef.current = true;

        // Lưu mã sản phẩm vừa chọn để bỏ qua search khi AutoComplete tự fill
        lastSelectedCodeRef.current = product.code;

        // Check if already added
        const details = watch('details');

        if (details.find(d => d.product_id === product.id))
        {
            notification.warning({message: 'Sản phẩm này đã được thêm'});
            setSearchKeyword("");
            // KHÔNG clear searchProducts để giữ danh sách hiển thị lại

            // Reset flag sau khi hoàn thành
            setTimeout(() => {
                isSelectingRef.current = false;
            }, 200);
            return;
        }

        const newDetail = {
            product_id: product.id,
            product_code: product.code,
            product_name: product.title,
            product_attribute: product.attribute_label,
            unit: product.unit,
            quantity: product.unit === 'kg' ? 0.1 : 1,
            stock: product.stock || 0,
        };

        append(newDetail);

        // Chỉ clear input, KHÔNG clear searchProducts
        setSearchKeyword("");

        // Reset flag sau khi hoàn thành (delay để đảm bảo mọi onChange đã settle)
        setTimeout(() => {
            isSelectingRef.current = false;
        }, 200);

        // searchProducts vẫn giữ nguyên để hiển thị lại khi click vào input
    }, [watch, append, searchProducts.length]);

    // Memoize options to prevent infinite re-renders
    const memoizedOptions = useMemo(() => {
        return searchProducts.map(item => ({
            ...item,
            label: <ProductOptionLabel product={item.product} />,
        }));
    }, [searchProducts]);

    const handleSaveDraft = async () =>
    {
        const details = watch('details');

        if (details.length === 0)
        {
            notification.error({message: 'Vui lòng thêm ít nhất 1 sản phẩm'});
            return;
        }

        const formData = watch();

        const payload = {
            code: formData.code || ''.trim(),
            purchase_date: formData.purchase_date.format('YYYY-MM-DD'),
            supplier_id: formData.supplier_id || 0,
            note: formData.note.trim(),
            status: 'draft',
            details: details.map(d => ({
                product_id: d.product_id,
                product_name: d.product_name,
                product_code: d.product_code,
                product_attribute: d.product_attribute,
                unit: d.unit,
                quantity: d.quantity,
            }))
        };

        setLoading(true);

        let [error, response] = await handleRequest(
            isEdit ? purchaseOrderApi.update({...payload, id}) : purchaseOrderApi.add(payload)
        );

        setLoading(false);

        if (!apiError(isEdit ? 'Cập nhật thất bại' : 'Tạo phiếu nhập hàng thất bại', error, response))
        {
            notification.success({message: 'Thành công', description: isEdit ? 'Cập nhật phiếu nhập hàng thành công' : 'Tạo phiếu nhập hàng thành công'});
            navigate('/purchase-order');
        }
    };

    const handleComplete = async () =>
    {
        const details = watch('details');

        if (details.length === 0)
        {
            notification.error({message: 'Vui lòng thêm ít nhất 1 sản phẩm'});
            return;
        }

        Modal.confirm({
            title: 'Hoàn thành phiếu nhập hàng',
            content: 'Khi hoàn thành, hàng sẽ được cập nhật vào tồn kho. Bạn có chắc chắn?',
            okText: 'Hoàn thành',
            cancelText: 'Hủy',
            onOk: async () => {
                const formData = watch();
                const payload = {
                    code: formData.code || ''.trim(),
                    purchase_date: formData.purchase_date.format('YYYY-MM-DD'),
                    supplier_id: formData.supplier_id || 0,
                    note: formData.note.trim(),
                    status: 'completed',
                    details: details.map(d => ({
                        product_id: d.product_id,
                        product_name: d.product_name,
                        product_code: d.product_code,
                        product_attribute: d.product_attribute,
                        unit: d.unit,
                        quantity: d.quantity,
                    }))
                };

                setLoading(true);

                let [error, response] = await handleRequest(
                    isEdit ? purchaseOrderApi.update({...payload, id}) : purchaseOrderApi.add(payload)
                );

                setLoading(false);

                if (!apiError('Hoàn thành phiếu nhập hàng thất bại', error, response))
                {
                    notification.success({message: 'Thành công', description: 'Phiếu nhập hàng đã được hoàn thành và cập nhật tồn kho'});
                    navigate('/purchase-order');
                }
            }
        });
    };

    const handleWeightChange = (index, weight) => {
        // Set quantity directly to weight value (0.1, 0.5, or 1)
        setValue(`details.${index}.quantity`, weight, { shouldDirty: true });
        // Force re-render to update the UI
        setForceUpdate(prev => prev + 1);
    };

    if (loading && isEdit && fields.length === 0) {
        return <Loading />;
    }

    // Watch all details to ensure re-render when selectedWeight changes
    const watchedDetails = watch('details');

    const detailColumns = [
        {
            title: 'Mã hàng',
            dataIndex: 'product_code',
            key: 'product_code',
            width: '100px',
        },
        {
            title: 'Tên hàng',
            dataIndex: 'product_name',
            key: 'product_name',
            width: '200px',
            render: (_, item, index) => (
                <>
                    <p className={'mb-0'}>{item.product_name} <b>{item.product_attribute}</b></p>
                </>
            )
        },
        {
            title: 'Số lượng',
            key: 'quantity',
            width: '200px',
            render: (_, item, index) => {
                // Get the current quantity from watched values
                const currentQuantity = watchedDetails[index]?.quantity || (item.unit === 'kg' ? 0.1 : 1);

                return (
                    <div>
                        {item.unit === 'kg' && (
                            <KgWeightSelector
                                selectedWeight={currentQuantity}
                                onWeightChange={(weight) => handleWeightChange(index, weight)}
                            />
                        )}
                        <Controller
                            control={control}
                            name={`details.${index}.quantity`}
                            render={({ field }) => (
                                <InputNumber
                                    {...field}
                                    value={field.value}
                                    min={item.unit === 'kg' ? 0.1 : 1}
                                    step={item.unit === 'kg' ? 0.1 : 1}
                                    precision={item.unit === 'kg' ? 1 : 0}
                                    style={{width: '100%'}}
                                    placeholder={item.unit === 'kg' ? 'VD: 0.6 = 600g, 1.4 = 1kg4' : ''}
                                />
                            )}
                        />
                    </div>
                );
            }
        },
        {
            title: 'Tồn kho',
            dataIndex: 'stock',
            key: 'stock',
            width: '100px',
        },
        {
            title: 'Đơn vị',
            dataIndex: 'unit',
            key: 'unit',
            width: '100px',
            render: (unit) => appData['units'][unit]?.label || unit,
        },
        {
            title: 'Hành động',
            key: 'action',
            width: '80px',
            render: (_, __, index) => (
                <DeleteOutlined
                    style={{cursor: 'pointer', color: '#ff4d4f'}}
                    onClick={() => remove(index)}
                />
            )
        }
    ];

    return (
        <>
            <ActionBar title={isEdit ? 'Cập nhật phiếu nhập hàng' : 'Tạo phiếu nhập hàng'}>
                <Button outline onClick={() => navigate('/purchase-order')}>Quay lại</Button>
            </ActionBar>
            <div className="container">
                <div className="content p-2">
                    <form className="form" onSubmit={handleSubmit(() => {})}>
                        {isSubmitting && <Loading />}
                        <Row gutter={20}>
                            {/* Left: Product list */}
                            <Col span={14}>
                                <div style={{marginBottom: '16px'}}>
                                    <label className="form-label">Tìm kiếm sản phẩm</label>
                                    <AutoComplete
                                        placeholder="Nhập mã hoặc tên sản phẩm"
                                        value={searchKeyword}
                                        onChange={handleSearchProducts}
                                        options={memoizedOptions}
                                        onSelect={handleAddProduct}
                                        filterOption={false}
                                        style={{width: '100%'}}
                                    />
                                </div>

                                {fields.length === 0 ? (
                                    <Empty description="Chưa có sản phẩm nào" />
                                ) : (
                                    <Table
                                        key={`table-${forceUpdate}`}
                                        columns={detailColumns}
                                        dataSource={fields.map((item, index) => ({key: index, ...item, ...watchedDetails[index]}))}
                                        pagination={false}
                                        size="small"
                                    />
                                )}
                            </Col>

                            {/* Right: Purchase order info */}
                            <Col span={10}>
                                <div className="form-box">
                                    <p className="heading">Thông tin phiếu nhập</p>

                                    <Controller control={control} name="code" render={({ field }) => (
                                        <InputField
                                            label="Mã phiếu nhập"
                                            errors={errors}
                                            {...field}
                                        />
                                    )} />

                                    <Controller control={control} name="purchase_date" render={({ field }) => (
                                        <DateField
                                            label="Ngày nhập hàng"
                                            errors={errors}
                                            {...field}
                                        />
                                    )} />

                                    <Controller control={control} name="supplier_id" render={({ field }) => (
                                        <SelectSupplier
                                            label="Nhà cung cấp"
                                            errors={errors}
                                            onSelect={(supplier) => {
                                                field.onChange(supplier?.id);
                                                if (supplier) {
                                                    setValue('supplier_name', supplier.name);
                                                }
                                            }}
                                            {...field}
                                        />
                                    )} />

                                    <Controller control={control} name="note" render={({ field }) => (
                                        <TextAreaField
                                            label="Ghi chú"
                                            rows={4}
                                            errors={errors}
                                            {...field}
                                        />
                                    )} />

                                    <div className="form-group d-flex justify-content-end">
                                        <Button
                                            background
                                            blue
                                            onClick={handleSaveDraft}
                                            disabled={isSubmitting}
                                        >Lưu tạm</Button>

                                        <Button
                                            background
                                            green
                                            onClick={handleComplete}
                                            disabled={isSubmitting}
                                        >Hoàn thành</Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </form>
                </div>
            </div>
        </>
    );
};

export default PurchaseOrderForm;
